import { useSyncExternalStore } from 'react'
import { LEGAL_VERSION } from './legal'

/**
 * HOZZAJARULAS-KEZELES.
 *
 * ────────────────────────────────────────────────────────────────────
 * MIRE VONATKOZIK
 *
 * EGYETLEN dologra: a MARKETINGCELU meresre (ma a Meta pixel). Nem
 * vonatkozik a mukodeshez szukseges tarolasra (TUS feltoltes-folytatas,
 * belso bejelentkezes), es nem vonatkozik a sajat, elso felkent vegzett
 * konverziomeresre (`voxora.meres`) sem - az a sajat kiszolgalonkon
 * marad, nem megy harmadik felhez, es a jogalapja jogos erdek.
 *
 * Egy kapcsolo, egy cel. Ha valaha tobb cel lesz, EZ a fajl bovul, nem
 * a hivo helyek.
 * ────────────────────────────────────────────────────────────────────
 */

export type Hozzajarulas = 'megadva' | 'megtagadva'

/**
 * ════════════════════════════════════════════════════════════════════
 * AZ ALAPERTELMEZETT ALLAPOT - EZ AZ EGYETLEN SOR, AMI ATALLITANDO
 * ════════════════════════════════════════════════════════════════════
 *
 * `'megadva'`    - a pixel a valasz ELOTT is fut (opt-out modell).
 * `'megtagadva'` - a pixel csak KIFEJEZETT elfogadas utan indul (opt-in).
 *
 * ────────────────────────────────────────────────────────────────────
 * A JOGI HELYZET, AMIT ERROL TUDNI KELL
 *
 * Az EU-ban es Magyarorszagon a MARKETINGCELU merest ELOZETES,
 * KIFEJEZETT hozzajarulas nelkul nem szabad elinditani. A ket
 * kulcsszabaly:
 *
 *   - GDPR 4. cikk 11. pont + 7. cikk: a hozzajarulas "egyertelmu,
 *     kifejezett akaratnyilvanitas". Az EUB a Planet49-ugyben
 *     (C-673/17) kimondta: az ELORE BEJELOLT negyzet, tehat a
 *     hallgatas mint beleegyezes, NEM ervenyes hozzajarulas.
 *   - ePrivacy-iranyelv 5. cikk (3): a vegberendezesen valo tarolashoz
 *     es az ott tarolt adathoz valo hozzaferesheez elozetes
 *     hozzajarulas kell, ha az nem technikailag szukseges.
 *
 * A NAIH ezt a gyakorlatban is szamon keri: a NAIH-3195-11/2022 szamu
 * hatarozat egy suti-hozzajarulasi keretrendszer miatt 10 millio Ft
 * birsagot szabott ki. Mas tagallamban a Meta pixel hozzajarulas
 * nelkuli hasznalatat tobb tizmillio euros nagysagrendben birsagoltak.
 *
 * ────────────────────────────────────────────────────────────────────
 * AMI EZEN NEM VALTOZTAT
 *
 * A `/sutik` oldal szovege EBBOL az ertekbol szarmazik, tehat sosem
 * allithat mast, mint ami technikailag tortenik. Az opt-out beallitas
 * adatvedelmi megfeleloseget kulon jogi donteskent kell kezelni: a kod
 * itt a tulajdonos altal kert tenyleges mukodest rogziti.
 *
 * A dontes az adatkezeloe. Ez a megjegyzes azert all itt, hogy a dontes
 * TUDATOS legyen, es hogy egy kesobbi olvaso ne velelmezze: valaki
 * egyszeruen elfelejtette atallitani.
 * ────────────────────────────────────────────────────────────────────
 */
export const ALAPERTELMEZETT_MARKETING: Hozzajarulas = 'megadva'

/** Beszedes nev: aki megnyitja a fejlesztoi eszkozoket, ertse, mi ez. */
const STORAGE_KEY = 'voxora.hozzajarulas'

interface Tarolt {
  marketing: Hozzajarulas
  /**
   * A valaszkor hatalyos jogi verzio.
   *
   * Ha a tajekoztato erdemben valtozik, a `LEGAL_VERSION` no, es a panel
   * UJRA megjelenik: egy uj celra vagy uj cimzettre a regi kattintas nem
   * szol. A KORABBI VALASZ ilyenkor is ervenyben marad addig, amig az uj
   * kerdesre nem jon valasz - kulonben egy elutasitast a verziovaltas
   * csendben visszaforditana elfogadassa.
   */
  verzio: string
  /** Mikor szuletett a valasz. Az elszamoltathatosaghoz (GDPR 7. cikk (1)). */
  ido: number
}

/** Ha a tarolo nem erheto el (privat mod, ceges hazirend), ez marad. */
let memoria: Tarolt | null = null

function olvas(): Tarolt | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return memoria

    const p = JSON.parse(raw) as Partial<Tarolt>
    if (p?.marketing !== 'megadva' && p?.marketing !== 'megtagadva') return memoria
    if (typeof p.verzio !== 'string' || typeof p.ido !== 'number') return memoria

    return { marketing: p.marketing, verzio: p.verzio, ido: p.ido }
  } catch {
    return memoria
  }
}

function ir(ertek: Tarolt | null): void {
  memoria = ertek
  try {
    if (ertek === null) localStorage.removeItem(STORAGE_KEY)
    else localStorage.setItem(STORAGE_KEY, JSON.stringify(ertek))
  } catch {
    // Tiltott vagy tele tarolas: a memoriaban tartott ertek marad ervenyes.
  }
}

export interface Allapot {
  /** Fut-e ma a marketingmeres. */
  marketing: Hozzajarulas
  /** Meg kell-e kerdezni a latogatot (nincs valasz, vagy uj jogi verzio). */
  kerdezzunk: boolean
}

/*
  A PILLANATKEP GYORSITOTARAZVA.

  A `useSyncExternalStore` megkoveteli, hogy a `getSnapshot` VALTOZATLAN
  hivatkozast adjon vissza, amig nem tortent valtozas. Uj objektumot
  gyartva minden renderben vegtelen ujrarenderelesbe futnank.
*/
let pillanat: Allapot | null = null

/**
 * A LATOGATO MAGA NYITOTTA VISSZA A PANELT.
 *
 * ────────────────────────────────────────────────────────────────────
 * MIERT NEM A TAROLT VALASZ TORLESE EZ
 *
 * Kezenfekvo lenne a "Suti-beallitasok" gombot a bejegyzes torlesere
 * kotni. Az viszont CSENDBEN VISSZAFORDITANA egy elutasitast: torles
 * utan az allapot az alapertelmezettre esne vissza, ami ma `megadva` -
 * tehat aki epp ellenorizni akarta a dontesét, azzal ujra elindulna a
 * meres, mielott barmit valaszolt volna.
 *
 * Ezert ez csak a KERDEST hozza vissza. A tarolt valasz ervenyben marad,
 * amig uj valasz nem szuletik.
 * ────────────────────────────────────────────────────────────────────
 */
let ujrakerdes = false

function szamol(): Allapot {
  const t = olvas()
  return {
    marketing: t?.marketing ?? ALAPERTELMEZETT_MARKETING,
    kerdezzunk: ujrakerdes || t === null || t.verzio !== LEGAL_VERSION
  }
}

export function allapot(): Allapot {
  if (pillanat === null) pillanat = szamol()
  return pillanat
}

/** Fut-e a marketingmeres. A pixel EZT az egy kerdest teszi fel. */
export function marketingEngedelyezett(): boolean {
  return allapot().marketing === 'megadva'
}

type Figyelo = () => void
const figyelok = new Set<Figyelo>()

function frissit(): void {
  pillanat = szamol()
  for (const fn of figyelok) fn()
}

/** A latogato valasza. Ez az EGYETLEN ut a hozzajarulas megadasahoz. */
export function valasz(marketing: Hozzajarulas): void {
  ujrakerdes = false
  ir({ marketing, verzio: LEGAL_VERSION, ido: Date.now() })
  frissit()
}

/**
 * A PANEL UJRANYITASA - a "Suti-beallitasok" hivatkozas ezt hivja.
 *
 * A visszavonasnak ugyanolyan konnyunek kell lennie, mint a
 * megadasnak (GDPR 7. cikk (3)), ezert a hivatkozas a lablecben es a
 * `/sutik` oldalon is ott van, minden aloldalrol egy kattintasra.
 */
export function panelUjranyitasa(): void {
  ujrakerdes = true
  frissit()
}

/**
 * A TAROLT VALASZ TORLESE.
 *
 * A `/sutik` oldal igeri, hogy a dontes visszavonhato es ujra
 * megkerdezheto. Torles utan a panel ujra eljon, es az allapot
 * visszaesik az alapertelmezettre.
 */
export function hozzajarulasTorlese(): void {
  ir(null)
  frissit()
}

/** A tarolt valasz idopontja, ha van. A `/sutik` oldal ezt mutatja meg. */
export function valaszIdopontja(): Date | null {
  const t = olvas()
  return t === null ? null : new Date(t.ido)
}

function feliratkozas(fn: Figyelo): () => void {
  figyelok.add(fn)
  return () => {
    figyelok.delete(fn)
  }
}

/** A hozzajarulas allapota Reactben. */
export function useHozzajarulas(): Allapot {
  return useSyncExternalStore(feliratkozas, allapot, allapot)
}

/** CSAK TESZTHEZ: a modulszintu gyorsitotar es a memoria uritese. */
export function __resetHozzajarulas(): void {
  memoria = null
  pillanat = null
  ujrakerdes = false
  figyelok.clear()
}
